<template>
  <button
    type="button"
    class="inline-action-feedback"
    :class="`inline-action-feedback--${state}`"
    :data-state="state"
    :data-tone="tone"
    :aria-label="currentAriaLabel"
    :aria-busy="busy ? 'true' : undefined"
    :disabled="disabled || busy"
    @click="handleActivation"
  >
    <span v-if="state === 'idle'" class="inline-action-feedback__content">
      <slot></slot>
    </span>
    <span
      v-else
      class="inline-action-feedback__message"
      :role="state === 'error' ? 'alert' : 'status'"
      :aria-live="state === 'error' ? 'assertive' : 'polite'"
      aria-atomic="true"
    >
      {{ currentMessage }}
    </span>
  </button>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";

const props = defineProps({
  action: {
    type: Function,
    required: true,
  },
  ariaLabel: {
    type: String,
    required: true,
  },
  confirmationMessage: {
    type: String,
    default: "",
  },
  confirmAriaLabel: {
    type: String,
    default: "",
  },
  confirmedMessage: {
    type: String,
    default: "",
  },
  errorMessage: {
    type: String,
    default: "",
  },
  confirmationDuration: {
    type: Number,
    default: 3000,
    validator: (value) => value >= 0,
  },
  feedbackDuration: {
    type: Number,
    default: 1800,
    validator: (value) => value >= 0,
  },
  tone: {
    type: String,
    default: "neutral",
    validator: (value) => ["neutral", "danger"].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["state-change", "success", "error"]);
const state = ref("idle");
const busy = ref(false);
let resetTimer = null;

const currentMessage = computed(() => {
  if (state.value === "confirming") return props.confirmationMessage;
  if (state.value === "confirmed") return props.confirmedMessage;
  if (state.value === "error") return props.errorMessage;
  return "";
});

const currentAriaLabel = computed(() => {
  if (state.value === "confirming") {
    return props.confirmAriaLabel || props.confirmationMessage;
  }
  return currentMessage.value || props.ariaLabel;
});

const clearResetTimer = () => {
  if (resetTimer === null) return;
  window.clearTimeout(resetTimer);
  resetTimer = null;
};

const setState = (nextState) => {
  if (state.value === nextState) return;
  state.value = nextState;
  emit("state-change", nextState);
};

const reset = () => {
  clearResetTimer();
  setState("idle");
};

const scheduleReset = (duration) => {
  clearResetTimer();
  if (duration === 0) {
    reset();
    return;
  }
  resetTimer = window.setTimeout(reset, duration);
};

const runAction = async () => {
  if (busy.value) return;
  busy.value = true;
  clearResetTimer();

  try {
    await props.action();
    emit("success");
    if (props.confirmedMessage) {
      setState("confirmed");
      scheduleReset(props.feedbackDuration);
    } else {
      setState("idle");
    }
  } catch (error) {
    emit("error", error);
    if (props.errorMessage) {
      setState("error");
      scheduleReset(props.feedbackDuration);
    } else {
      setState("idle");
    }
  } finally {
    busy.value = false;
  }
};

const handleActivation = () => {
  if (props.disabled || busy.value) return;

  if (props.confirmationMessage && state.value !== "confirming") {
    setState("confirming");
    scheduleReset(props.confirmationDuration);
    return;
  }

  void runAction();
};

watch(
  () => props.disabled,
  (isDisabled) => {
    if (isDisabled) reset();
  },
);

onBeforeUnmount(clearResetTimer);

defineExpose({ reset });
</script>

<style scoped>
.inline-action-feedback {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  appearance: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: inherit;
  cursor: pointer;
}

.inline-action-feedback:disabled {
  cursor: wait;
}

.inline-action-feedback:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}

button.inline-action-feedback:hover {
  transform: none;
  box-shadow: none;
}

.inline-action-feedback__content {
  display: contents;
}

.inline-action-feedback__message {
  overflow-wrap: anywhere;
  font-weight: var(--font-weight-bold);
}

.inline-action-feedback--confirming[data-tone="danger"] {
  color: var(--color-feedback-danger-foreground);
}
</style>
